import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UsersIcon, 
  CurrencyDollarIcon, 
  ShoppingCartIcon,
  AcademicCapIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ArrowUpTrayIcon,
  DocumentArrowDownIcon,
  BookOpenIcon,
  BeakerIcon,
  RocketLaunchIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  ArrowPathRoundedSquareIcon,
  GiftIcon,
  MegaphoneIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import ModuleEditor from '../components/editors/ModuleEditor';
import LabEditor from '../components/editors/LabEditor';
import CapstoneEditor from '../components/editors/CapstoneEditor';
import UserManagement from '../components/admin/UserManagement';
import PrerequisitesEditor from '../components/admin/PrerequisitesEditor';
import InstructorManagement from '../components/admin/InstructorManagement';
import CouponManagement from './admin/CouponManagement';
import BannerManagement from './admin/BannerManagement';

const Admin = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Content management state
  const [contentTab, setContentTab] = useState('modules'); // modules, labs, capstones
  const [modules, setModules] = useState([]);
  const [labs, setLabs] = useState([]);
  const [capstones, setCapstones] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);
  
  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editorType, setEditorType] = useState(null); // 'module', 'lab', 'capstone'
  
  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'content') {
      fetchContent();
    }
  }, [activeTab, contentTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsRes, studentsRes, purchasesRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/students'),
        api.get('/admin/purchases')
      ]);

      setStats(statsRes.data);
      setStudents(studentsRes.data.data || []);
      setPurchases(purchasesRes.data.data || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      if (error.response?.status === 403) {
        setError('Access Denied: You do not have admin privileges.');
      } else if (error.response?.status === 401) {
        setError('Not authenticated. Please log in again.');
      } else {
        setError(`Failed to load admin data: ${error.response?.data?.message || error.message || 'Please try again.'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchContent = async () => {
    try {
      setContentLoading(true);
      if (contentTab === 'modules') {
        const res = await api.get('/admin/modules');
        setModules(res.data);
      } else if (contentTab === 'labs') {
        const res = await api.get('/admin/labs');
        setLabs(res.data);
      } else if (contentTab === 'capstones') {
        const res = await api.get('/admin/capstones');
        setCapstones(res.data);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      setError('Failed to load content');
    } finally {
      setContentLoading(false);
    }
  };

  const handleDeleteContent = async (type, id) => {
    if (!confirm(`Are you sure you want to delete this ${type}?`)) {
      return;
    }

    try {
      await api.delete(`/admin/${type}/${id}`);
      fetchContent();
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      alert(error.response?.data?.message || `Failed to delete ${type}`);
    }
  };

  const handleTogglePublish = async (type, id) => {
    try {
      await api.post(`/admin/${type}/${id}/toggle-publish`);
      fetchContent();
    } catch (error) {
      console.error(`Error toggling publish status:`, error);
      alert(`Failed to toggle publish status`);
    }
  };

  const handleOpenEditor = (type, item = null) => {
    setEditorType(type);
    setEditingItem(item);
    setEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setEditorOpen(false);
    setEditingItem(null);
    setEditorType(null);
  };

  const handleSaveContent = (savedItem) => {
    fetchContent(); // Refresh the list
  };

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setImportFile(file);
      setImportResults(null);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Please select a file first');
      return;
    }

    setImporting(true);
    setImportResults(null);

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const response = await api.post('/admin/import/students', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResults(response.data.results);
      setImportFile(null);
      
      // Refresh student list
      const studentsRes = await api.get('/admin/students');
      setStudents(studentsRes.data.data || []);
      
      // Update stats
      const statsRes = await api.get('/admin/stats');
      setStats(statsRes.data);
    } catch (error) {
      console.error('Import error:', error);
      alert(`Import failed: ${error.response?.data?.message || error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const baseURL = import.meta.env.VITE_API_URL || '/api';
      const token = localStorage.getItem('auth-token');
      
      // Build the full URL with auth token as query parameter for download
      const url = `${baseURL}/admin/import/template`;
      
      // Use fetch with credentials to download the file
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/csv',
        },
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to download template');
      }
      
      // Create blob and download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'student_import_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download template:', error);
      alert('Failed to download template. Please try again or contact support.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
        <p className="text-sm sm:text-base text-gray-600">Manage students, view purchases, and track revenue</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Total Students</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">{stats?.total_students || 0}</p>
            </div>
            <div className="bg-blue-100 p-2 sm:p-3 rounded-lg flex-shrink-0">
              <UsersIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Total Revenue</p>
              <p className="text-2xl sm:text-3xl font-bold text-green-600">${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="bg-green-100 p-2 sm:p-3 rounded-lg flex-shrink-0">
              <CurrencyDollarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Total Purchases</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">{stats?.total_purchases || 0}</p>
            </div>
            <div className="bg-purple-100 p-2 sm:p-3 rounded-lg flex-shrink-0">
              <ShoppingCartIcon className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Enrolled Students</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">{stats?.enrolled_students || 0}</p>
            </div>
            <div className="bg-yellow-100 p-2 sm:p-3 rounded-lg flex-shrink-0">
              <AcademicCapIcon className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Revenue by Tier */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-6 sm:mb-8">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
          <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-blue-600" />
          Revenue by Tier
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
          {stats?.revenue_by_tier?.map((tier) => (
            <div key={tier.tier} className="border border-gray-200 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-gray-600 capitalize mb-1">{tier.tier}</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">${tier.total.toFixed(2)}</p>
              <p className="text-[10px] sm:text-xs text-gray-500 mt-1">{tier.count} sales</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 shadow-sm mb-4 sm:mb-6 -mx-3 sm:mx-0">
        <nav className="flex flex-wrap gap-x-2 sm:gap-x-4 lg:gap-x-6 gap-y-2 px-3 sm:px-6 py-2">
          <button
            onClick={() => setActiveTab('overview')}
            className={`${
              activeTab === 'overview'
                ? 'border-blue-600 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'
            } flex items-center gap-1.5 sm:gap-2 whitespace-nowrap py-2.5 sm:py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-150 flex-shrink-0`}
          >
            <ChartBarIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('students')}
            className={`${
              activeTab === 'students'
                ? 'border-blue-600 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'
            } flex items-center gap-1.5 sm:gap-2 whitespace-nowrap py-2.5 sm:py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-150 flex-shrink-0`}
          >
            <UsersIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Students
            <span className="ml-0.5 sm:ml-1 px-1.5 sm:px-2 py-0.5 bg-gray-200 text-gray-700 text-[10px] sm:text-xs rounded-full">
              {students.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('purchases')}
            className={`${
              activeTab === 'purchases'
                ? 'border-blue-600 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'
            } flex items-center gap-1.5 sm:gap-2 whitespace-nowrap py-2.5 sm:py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-150 flex-shrink-0`}
          >
            <ShoppingCartIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Purchases
            <span className="ml-0.5 sm:ml-1 px-1.5 sm:px-2 py-0.5 bg-gray-200 text-gray-700 text-[10px] sm:text-xs rounded-full">
              {purchases.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`${
              activeTab === 'users'
                ? 'border-blue-600 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'
            } flex items-center gap-1.5 sm:gap-2 whitespace-nowrap py-2.5 sm:py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-150 flex-shrink-0`}
          >
            <UserGroupIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Users
          </button>
          <button
            onClick={() => setActiveTab('import')}
            className={`${
              activeTab === 'import'
                ? 'border-blue-600 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'
            } flex items-center gap-1.5 sm:gap-2 whitespace-nowrap py-2.5 sm:py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-150 flex-shrink-0`}
          >
            <ArrowUpTrayIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Import
          </button>
          <button
            onClick={() => setActiveTab('content')}
            className={`${
              activeTab === 'content'
                ? 'border-blue-600 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'
            } flex items-center gap-1.5 sm:gap-2 whitespace-nowrap py-2.5 sm:py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-150 flex-shrink-0`}
          >
            <BookOpenIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Content
          </button>
          <button
            onClick={() => setActiveTab('prerequisites')}
            className={`${
              activeTab === 'prerequisites'
                ? 'border-blue-600 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'
            } flex items-center gap-2 whitespace-nowrap py-3 px-4 border-b-2 font-medium text-sm transition-colors duration-150`}
          >
            <ArrowPathRoundedSquareIcon className="w-4 h-4" />
            Prerequisites
          </button>
          <button
            onClick={() => setActiveTab('instructors')}
            className={`${
              activeTab === 'instructors'
                ? 'border-blue-600 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'
            } flex items-center gap-2 whitespace-nowrap py-3 px-4 border-b-2 font-medium text-sm transition-colors duration-150`}
          >
            <AcademicCapIcon className="w-4 h-4" />
            Instructors
          </button>
          <button
            onClick={() => setActiveTab('coupons')}
            className={`${
              activeTab === 'coupons'
                ? 'border-blue-600 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'
            } flex items-center gap-2 whitespace-nowrap py-3 px-4 border-b-2 font-medium text-sm transition-colors duration-150`}
          >
            <GiftIcon className="w-4 h-4" />
            Coupons
          </button>
          <button
            onClick={() => setActiveTab('banners')}
            className={`${
              activeTab === 'banners'
                ? 'border-blue-600 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'
            } flex items-center gap-2 whitespace-nowrap py-3 px-4 border-b-2 font-medium text-sm transition-colors duration-150`}
          >
            <MegaphoneIcon className="w-4 h-4" />
            Banners
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Course Completion Stats</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-2">Modules Completed</p>
              <p className="text-3xl font-bold text-blue-600">{stats?.total_modules_completed || 0}</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-2">Labs Completed</p>
              <p className="text-3xl font-bold text-green-600">{stats?.total_labs_completed || 0}</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-2">Capstones Completed</p>
              <p className="text-3xl font-bold text-purple-600">{stats?.total_capstones_completed || 0}</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'students' && (
        <div className="bg-white rounded-lg shadow">
          {/* Search */}
          <div className="p-6 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Students Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Enrolled
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{student.name}</div>
                        <div className="text-sm text-gray-500">{student.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 capitalize">
                        {student.purchased_tier || 'None'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Modules: {student.modules_completed || 0} | Labs: {student.labs_completed || 0} | Capstones: {student.capstones_completed || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {student.enrolled_at ? new Date(student.enrolled_at).toLocaleDateString() : 'Not enrolled'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'purchases' && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {purchases.map((purchase) => (
                <tr key={purchase.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    #{purchase.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{purchase.user?.name}</div>
                    <div className="text-sm text-gray-500">{purchase.user?.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 capitalize">
                      {purchase.tier}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    ${purchase.amount_dollars?.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      purchase.status === 'completed' ? 'bg-green-100 text-green-800' :
                      purchase.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {purchase.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(purchase.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'users' && (
        <UserManagement />
      )}

      {activeTab === 'import' && (
        <div className="space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">How to Import Students</h3>
            <ol className="list-decimal list-inside space-y-2 text-blue-800">
              <li>Download the CSV template below</li>
              <li>Fill in student information (name, email, tier)</li>
              <li>Upload the completed CSV file</li>
              <li>Review the import results</li>
              <li>Students will receive welcome emails with password reset links</li>
            </ol>
            <div className="mt-4">
              <p className="text-sm text-blue-700 font-medium mb-2">CSV Format:</p>
              <code className="block bg-white p-2 rounded border border-blue-200 text-sm">
                name,email,tier<br/>
                John Doe,john@example.com,pro<br/>
                Jane Smith,jane@example.com,basic
              </code>
              <p className="text-sm text-blue-700 mt-3">
                <strong>Note:</strong> Imported students will receive an automatic welcome email 
                with a password reset link to set their own password.
              </p>
            </div>
          </div>

          {/* Template Download */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Step 1: Download Template</h3>
            <button
              onClick={downloadTemplate}
              className="inline-flex items-center px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
              Download CSV Template
            </button>
          </div>

          {/* File Upload */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Step 2: Upload CSV File</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  disabled={importing}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100
                    disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              {importFile && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Selected file:</span> {importFile.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Size: {(importFile.size / 1024).toFixed(2)} KB
                  </p>
                </div>
              )}

              <button
                onClick={handleImport}
                disabled={!importFile || importing}
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                    Import Students
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Import Results */}
          {importResults && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Results</h3>
              
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Total Rows</p>
                  <p className="text-2xl font-bold text-gray-900">{importResults.total}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600">Successful</p>
                  <p className="text-2xl font-bold text-green-600">{importResults.success.length}</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <p className="text-sm text-yellow-600">Duplicates</p>
                  <p className="text-2xl font-bold text-yellow-600">{importResults.duplicates.length}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-sm text-red-600">Errors</p>
                  <p className="text-2xl font-bold text-red-600">{importResults.errors.length}</p>
                </div>
              </div>

              {/* Success Details */}
              {importResults.success.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-medium text-green-900 mb-2">✅ Successfully Imported ({importResults.success.length})</h4>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="mb-3 pb-3 border-b border-green-200">
                      <p className="text-sm text-green-700 flex items-center">
                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Welcome emails with password reset links have been sent to all imported students
                      </p>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      <ul className="space-y-2 text-sm">
                        {importResults.success.map((item, index) => (
                          <li key={index} className="text-green-800">
                            Row {item.row}: {item.user.name} ({item.user.email}) - {item.user.tier}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Duplicates */}
              {importResults.duplicates.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-medium text-yellow-900 mb-2">⚠️ Duplicates Skipped ({importResults.duplicates.length})</h4>
                  <div className="bg-yellow-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                    <ul className="space-y-2 text-sm">
                      {importResults.duplicates.map((item, index) => (
                        <li key={index} className="text-yellow-800">
                          Row {item.row}: {item.data.email} (already exists as {item.existing_user.name})
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Errors */}
              {importResults.errors.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-900 mb-2">❌ Errors ({importResults.errors.length})</h4>
                  <div className="bg-red-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                    <ul className="space-y-2 text-sm">
                      {importResults.errors.map((item, index) => (
                        <li key={index} className="text-red-800">
                          Row {item.row}: {item.message}
                          {item.data && (
                            <span className="text-red-600"> - {JSON.stringify(item.data)}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'content' && (
        <div className="space-y-6">
          {/* Content Type Tabs */}
          <div className="bg-white rounded-lg shadow">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 px-6">
                <button
                  onClick={() => setContentTab('modules')}
                  className={`${
                    contentTab === 'modules'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <BookOpenIcon className="h-5 w-5 mr-2" />
                  Modules
                </button>
                <button
                  onClick={() => setContentTab('labs')}
                  className={`${
                    contentTab === 'labs'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <BeakerIcon className="h-5 w-5 mr-2" />
                  Labs
                </button>
                <button
                  onClick={() => setContentTab('capstones')}
                  className={`${
                    contentTab === 'capstones'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <RocketLaunchIcon className="h-5 w-5 mr-2" />
                  Capstones
                </button>
              </nav>
            </div>

            {/* Content List */}
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900 capitalize">
                  {contentTab}
                </h3>
                <button
                  onClick={() => handleOpenEditor(contentTab)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create {contentTab === 'modules' ? 'Module' : contentTab === 'labs' ? 'Lab' : 'Capstone'}
                </button>
              </div>

              {contentLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {contentTab === 'modules' && modules.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No modules yet. Create your first module!</p>
                  )}
                  {contentTab === 'modules' && modules.map((module) => (
                    <div key={module.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-lg font-semibold text-gray-900">{module.title}</h4>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              module.is_published 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {module.is_published ? 'Published' : 'Draft'}
                            </span>
                            {module.difficulty_level && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 capitalize">
                                {module.difficulty_level}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{module.description}</p>
                          <div className="flex gap-4 text-xs text-gray-500">
                            <span>Code: {module.code}</span>
                            <span>Order: {module.order}</span>
                            {module.duration_minutes && <span>{module.duration_minutes} min</span>}
                            <span>{module.labs_count || 0} Labs</span>
                            <span>{module.case_studies_count || 0} Case Studies</span>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => handleTogglePublish('modules', module.id)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title={module.is_published ? 'Unpublish' : 'Publish'}
                          >
                            {module.is_published ? '👁️' : '👁️‍🗨️'}
                          </button>
                          <button
                            onClick={() => handleOpenEditor('modules', module)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteContent('modules', module.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {contentTab === 'labs' && labs.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No labs yet. Create your first lab!</p>
                  )}
                  {contentTab === 'labs' && labs.map((lab) => (
                    <div key={lab.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-lg font-semibold text-gray-900">{lab.title}</h4>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              lab.is_published 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {lab.is_published ? 'Published' : 'Draft'}
                            </span>
                            {lab.difficulty && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 capitalize">
                                {lab.difficulty}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{lab.description}</p>
                          <div className="flex gap-4 text-xs text-gray-500">
                            <span>Code: {lab.code}</span>
                            <span>Module: {lab.module?.title || 'N/A'}</span>
                            <span>Order: {lab.order}</span>
                            {lab.estimated_time_minutes && <span>~{lab.estimated_time_minutes} min</span>}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => handleTogglePublish('labs', lab.id)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title={lab.is_published ? 'Unpublish' : 'Publish'}
                          >
                            {lab.is_published ? '👁️' : '👁️‍🗨️'}
                          </button>
                          <button
                            onClick={() => handleOpenEditor('labs', lab)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteContent('labs', lab.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {contentTab === 'capstones' && capstones.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No capstones yet. Create your first capstone!</p>
                  )}
                  {contentTab === 'capstones' && capstones.map((capstone) => (
                    <div key={capstone.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-lg font-semibold text-gray-900">{capstone.title}</h4>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              capstone.is_published 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {capstone.is_published ? 'Published' : 'Draft'}
                            </span>
                            {capstone.difficulty && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800 capitalize">
                                {capstone.difficulty}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{capstone.description}</p>
                          <div className="flex gap-4 text-xs text-gray-500">
                            <span>Code: {capstone.code}</span>
                            <span>Order: {capstone.order}</span>
                            {capstone.estimated_hours && <span>~{capstone.estimated_hours} hrs</span>}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => handleTogglePublish('capstones', capstone.id)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title={capstone.is_published ? 'Unpublish' : 'Publish'}
                            >
                            {capstone.is_published ? '👁️' : '👁️‍🗨️'}
                          </button>
                          <button
                            onClick={() => handleOpenEditor('capstones', capstone)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteContent('capstones', capstone.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Prerequisites Tab */}
      {activeTab === 'prerequisites' && (
        <PrerequisitesEditor />
      )}
      
      {/* Instructors Tab */}
      {activeTab === 'instructors' && (
        <InstructorManagement />
      )}

      {activeTab === 'coupons' && (
        <CouponManagement />
      )}

      {activeTab === 'banners' && (
        <BannerManagement />
      )}
      
      {/* Editor Modals */}
      {editorOpen && editorType === 'modules' && (
        <ModuleEditor 
          module={editingItem} 
          onClose={handleCloseEditor} 
          onSave={handleSaveContent} 
        />
      )}
      
      {editorOpen && editorType === 'labs' && (
        <LabEditor 
          lab={editingItem} 
          onClose={handleCloseEditor} 
          onSave={handleSaveContent} 
        />
      )}
      
      {editorOpen && editorType === 'capstones' && (
        <CapstoneEditor 
          capstone={editingItem} 
          onClose={handleCloseEditor} 
          onSave={handleSaveContent} 
        />
      )}
    </div>
  );
};

export default Admin;
